const ISO_ALIASES: Record<string, string[]> = {
  CAISO: ["CAISO"],
  ERCOT: ["ERCOT"],
  ISONE: ["ISONE", "NEISO"],
  NYISO: ["NYISO"],
  PJM: ["PJM", "PJMISO"],
};

const OBJECT_PRIORITY_BY_ISO: Record<string, string[]> = {
  CAISO: ["NP15", "SP15", "ZP26"],
  NYISO: ["NYISO", "NYC"],
  PJM: ["RTO COMBINED", "MID ATLANTIC", "SOUTHERN", "WESTERN", "PJMISO"],
};

const OBJECT_NAME_ALIASES: Record<string, string> = {
  "MID-ATLANTIC REGION": "MID ATLANTIC",
  "MID ATLANTIC REGION": "MID ATLANTIC",
  "SOUTHERN REGION": "SOUTHERN",
  "WESTERN REGION": "WESTERN",
};

const SERIES_COLORS = [
  "#1e88e5",
  "#1f2a99",
  "#e67e22",
  "#16a34a",
  "#9333ea",
  "#0ea5e9",
  "#dc2626",
  "#4f46e5",
];

export const normalizeObjectName = (value: string | null | undefined): string | null => {
  const normalized = value?.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  return OBJECT_NAME_ALIASES[normalized] ?? normalized;
};

const normalizeSelectedIso = (selectedIso: string): string => {
  const normalized = selectedIso.trim().toUpperCase();
  if (!normalized) {
    return normalized;
  }

  if (normalized === "PJMISO") {
    return "PJM";
  }

  return normalized;
};

export const getLoadZoneMaxItems = (selectedIso: string): number => {
  const normalizedIso = normalizeSelectedIso(selectedIso);
  if (normalizedIso === "PJM") {
    return 4;
  }

  return 3;
};

export const matchesSelectedIso = (rowIso: string | undefined, selectedIso: string): boolean => {
  const rowIsoUpper = rowIso?.trim().toUpperCase();
  const selectedIsoUpper = selectedIso.trim().toUpperCase();

  if (!rowIsoUpper || !selectedIsoUpper) {
    return false;
  }

  if (rowIsoUpper === selectedIsoUpper) {
    return true;
  }

  const allowedIsos = ISO_ALIASES[selectedIsoUpper] ?? [selectedIsoUpper];
  return allowedIsos.includes(rowIsoUpper);
};

export const pickTopLoadZoneObjects = (
  objectTotals: Map<string, number>,
  selectedIso: string,
  maxItems = 3
): string[] => {
  const objectNames = Array.from(objectTotals.keys());
  const normalizedIso = normalizeSelectedIso(selectedIso);
  const objectPriority = OBJECT_PRIORITY_BY_ISO[normalizedIso] ?? [];

  return objectNames
    .sort((left, right) => {
      const leftPriority = objectPriority.indexOf(left);
      const rightPriority = objectPriority.indexOf(right);

      const leftRank = leftPriority === -1 ? Number.MAX_SAFE_INTEGER : leftPriority;
      const rightRank = rightPriority === -1 ? Number.MAX_SAFE_INTEGER : rightPriority;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return (objectTotals.get(right) ?? 0) - (objectTotals.get(left) ?? 0);
    })
    .slice(0, maxItems);
};

export const getLoadZoneSeriesColor = (index: number): string =>
  SERIES_COLORS[index % SERIES_COLORS.length];
